import { gettext, interpolate, ngettext } from "./translation";
// translated, human-readable time strings
export function getElapsedTimeString(pastDate) {
    const secondsElapsed = (Date.now() - pastDate.getTime()) / 1000;
    if (secondsElapsed < 60) {
        return gettext("now");
    }
    else if (secondsElapsed < 60 * 60) {
        const minutesElapsed = Math.floor(secondsElapsed / 60);
        return interpolate(ngettext("%(minutes)s minute ago", "%(minutes)s minutes ago", minutesElapsed), { minutes: minutesElapsed }, true);
    }
    else if (secondsElapsed <= 60 * 60 * 24) {
        const hoursElapsed = Math.floor(secondsElapsed / (60 * 60));
        return interpolate(ngettext("%(hours)s hour ago", "%(hours)s hours ago", hoursElapsed), { hours: hoursElapsed }, true);
    }
    else if (secondsElapsed < 60 * 60 * 24 * 7) {
        const daysElapsed = Math.floor(secondsElapsed / (60 * 60 * 24));
        return interpolate(ngettext("%(days)s day ago", "%(days)s days ago", daysElapsed), { days: daysElapsed }, true);
    }
    return pastDate.toLocaleDateString();
}
// shorthand time strings of format "1h2m3s"
export function getFormattedRelativeTimespan(secondsLeft, includeSeconds = true) {
    if (secondsLeft <= 0) {
        return "";
    }
    const hours = Math.floor(secondsLeft / 3600);
    secondsLeft = secondsLeft % 3600;
    const minutes = Math.floor(secondsLeft / 60);
    secondsLeft = secondsLeft % 60;
    let hourString = "";
    if (hours > 1) {
        hourString = `${hours}h`;
    }
    else if (hours === 1) {
        hourString = `${hours}h`;
    }
    let minuteString = "";
    if (minutes >= 1) {
        minuteString = `${minutes}m`;
    }
    else if (minutes === 0) {
        minuteString = includeSeconds ? "" : "<1m";
    }
    if (hours > 0 && minutes > 0) {
        minuteString = ` ${minuteString}`;
    }
    const secondString = includeSeconds ? `${secondsLeft}s` : "";
    return `${hourString}${minuteString}${secondString}`;
}
export function dateNSecondsAgo(seconds) {
    return new Date(Date.now() - seconds * 1000);
}
// Note this can return a negative result if date1 is earlier than date2
export function timeDifferenceSeconds(date1, date2) {
    return (date1.getTime() - date2.getTime()) / 1000;
}
export function minFromEpochTimestamp(epochTimestamp) {
    return (Date.now() - epochTimestamp) / (1000 * 60);
}
export function secondsToStr(totalSeconds) {
    const numberEnding = (num) => {
        return num > 1 ? "s" : "";
    };
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds %= 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        if (minutes > 0) {
            return `${hours} hour${numberEnding(hours)}, ${minutes} minute${numberEnding(minutes)}`;
        }
        return `${hours} hour${numberEnding(hours)}`;
    }
    if (minutes > 0) {
        if (seconds > 0) {
            return `${minutes} minute${numberEnding(minutes)}, ${seconds} second${numberEnding(seconds)}`;
        }
        return `${minutes} minute${numberEnding(minutes)}`;
    }
    if (seconds > 0) {
        return `${seconds} second${numberEnding(seconds)}`;
    }
    return "less than a second";
}
const i18n = {
    seconds: (seconds, abbrev = false) => {
        if (abbrev) {
            return interpolate(gettext("%(seconds)s sec"), { seconds: seconds }, true);
        }
        else {
            return interpolate(ngettext("%(seconds)s second", "%(seconds)s seconds", seconds), { seconds: seconds }, true);
        }
    },
    secondsAgo: (seconds) => {
        return interpolate(ngettext("%(seconds)s second ago", "%(seconds)s seconds ago", seconds), { seconds: seconds }, true);
    },
    secondsFromNow: (seconds) => {
        return interpolate(ngettext("%(seconds)s second from now", "%(seconds)s seconds from now", seconds), { seconds: seconds }, true);
    },
    minutes: (minutes, abbrev = false) => {
        if (abbrev) {
            return interpolate(ngettext("%(minutes)s min", "%(minutes)s mins", minutes), { minutes: minutes }, true);
        }
        return interpolate(ngettext("%(minutes)s minute", "%(minutes)s minutes", minutes), { minutes: minutes }, true);
    },
    minutesAgo: (minutes) => {
        return interpolate(ngettext("%(minutes)s minute ago", "%(minutes)s minutes ago", minutes), { minutes: minutes }, true);
    },
    minutesFromNow: (minutes) => {
        return interpolate(ngettext("%(minutes)s minute from now", "%(minutes)s minutes from now", minutes), { minutes: minutes }, true);
    },
    hours: (hours, abbrev = false) => {
        if (abbrev) {
            const hr = hours.toFixed(1);
            return interpolate(ngettext("%(hours)s hr", "%(hours)s hrs", hours), { hours: hr }, true);
        }
        return interpolate(ngettext("%(hours)s hour ago", "%(hours)s hours ago", hours), { hours: hours }, true);
    },
    hoursAgo: (hours) => {
        return interpolate(ngettext("%(hours)s hour ago", "%(hours)s hours ago", hours), { hours: hours }, true);
    },
    hoursFromNow: (hours) => {
        return interpolate(ngettext("%(hours)s hour from now", "%(hours)s hours from now", hours), { hours: hours }, true);
    },
    days: (days, abbrev = false) => {
        if (abbrev) {
            return interpolate(ngettext("%(days)s d", "%(days)s d", days), { days: days }, true);
        }
        return interpolate(ngettext("%(days)s day", "%(days)s days", days), { days: days }, true);
    },
    daysAgo: (days) => {
        return interpolate(ngettext("%(days)s day ago", "%(days)s days ago", days), { days: days }, true);
    },
    daysFromNow: (days) => {
        return interpolate(ngettext("%(days)s day from now", "%(days)s days from now", days), { days: days }, true);
    },
    weeks: (weeks, abbrev = false) => {
        if (abbrev) {
            return interpolate(ngettext("%(weeks)s wk", "%(weeks)s wks", weeks), { weeks: weeks }, true);
        }
        return interpolate(ngettext("%(weeks)s week", "%(weeks)s weeks", weeks), { weeks: weeks }, true);
    },
    weeksAgo: (weeks) => {
        return interpolate(ngettext("%(weeks)s week ago", "%(weeks)s weeks ago", weeks), { weeks: weeks }, true);
    },
    weeksFromNow: (weeks) => {
        return interpolate(ngettext("%(weeks)s week from now", "%(weeks)s weeks from now", weeks), { weeks: weeks }, true);
    },
    months: (months, abbrev = false) => {
        if (abbrev) {
            return interpolate(ngettext("%(months)s mo", "%(months)s mos", months), { months: months }, true);
        }
        return interpolate(ngettext("%(months)s month", "%(months)s months", months), { months: months }, true);
    },
    monthsAgo: (months) => {
        return interpolate(ngettext("%(months)s month ago", "%(months)s months ago", months), { months: months }, true);
    },
    monthsFromNow: (months) => {
        return interpolate(ngettext("%(months)s month from now", "%(months)s months from now", months), { months: months }, true);
    },
    years: (years, abbrev = false) => {
        if (abbrev) {
            return interpolate(ngettext("%(years)s yr", "%(years)s yrs", years), { years: years }, true);
        }
        return interpolate(ngettext("%(years)s year", "%(years)s years", years), { years: years }, true);
    },
    yearsAgo: (years) => {
        return interpolate(ngettext("%(years)s year ago", "%(years)s years ago", years), { years: years }, true);
    },
    yearsFromNow: (years) => {
        return interpolate(ngettext("%(years)s year from now", "%(years)s years from now", years), { years: years }, true);
    },
    now: gettext("now"),
    never: gettext("Never"),
};
const TIME_CHUNKS = [
    {
        name: "year",
        seconds: 60 * 60 * 24 * 365,
        translation: i18n.years,
        ago: i18n.yearsAgo,
        fromNow: i18n.yearsFromNow,
    },
    {
        name: "month",
        seconds: 60 * 60 * 24 * 30,
        translation: i18n.months,
        ago: i18n.monthsAgo,
        fromNow: i18n.monthsFromNow,
    },
    {
        name: "week",
        seconds: 60 * 60 * 24 * 7,
        translation: i18n.weeks,
        ago: i18n.weeksAgo,
        fromNow: i18n.weeksFromNow,
    },
    {
        name: "day",
        seconds: 60 * 60 * 24,
        translation: i18n.days,
        ago: i18n.daysAgo,
        fromNow: i18n.daysFromNow,
    },
    {
        name: "hour",
        seconds: 60 * 60,
        translation: i18n.hours,
        ago: i18n.hoursAgo,
        fromNow: i18n.hoursFromNow,
    },
    {
        name: "minute",
        seconds: 60,
        translation: i18n.minutes,
        ago: i18n.minutesAgo,
        fromNow: i18n.minutesFromNow,
    },
    {
        name: "second",
        seconds: 1,
        translation: i18n.seconds,
        ago: i18n.secondsAgo,
        fromNow: i18n.secondsFromNow,
    },
];
const HOUR_INDEX = TIME_CHUNKS.findIndex((chunk) => chunk.name === "hour");
const MINUTE_INDEX = TIME_CHUNKS.findIndex((chunk) => chunk.name === "minute");
const pluralize = (count, ending = "s") => {
    if (count === 1) {
        return "";
    }
    return ending;
};
const ending = (isFuture) => {
    return isFuture ? "from now" : "ago";
};
// WARNING: the output of this function is NOT LOCALIZED! TODO: https://multimediallc.leankit.com/card/30502080246243
/**
 * @param now what to treat as the current time
 * @param then the time that is being described in relation to `now`
 * @returns an English string describing the duration since/until a time.  IF the elapsed time is greater than
 * 24 hours, the string may contain up to two time units, otherwise it will only contain one.
 * Possible time units span from seconds to years, and are not abbreviated.
 * Ex: "1 day, 2 hours ago"; "3 months, 1 week from now"; "20 hours ago"; "1 minute from now"; "32 seconds ago"
 */
export function naturalTime(now, then) {
    const elapsed = then.getTime() - now.getTime();
    if (elapsed === 0) {
        return i18n.now;
    }
    const deltaSeconds = Math.floor(Math.abs(elapsed) / 1000);
    const isFuture = elapsed > 0;
    if (deltaSeconds < 60) {
        return `${deltaSeconds} second${pluralize(deltaSeconds)} ${ending(isFuture)}`;
    }
    let count = 0;
    let seconds = 0;
    let result = "";
    let i;
    for (i = 0; i < TIME_CHUNKS.length; i += 1) {
        count = Math.floor(deltaSeconds / TIME_CHUNKS[i].seconds);
        seconds = TIME_CHUNKS[i].seconds;
        if (count !== 0) {
            result = `${count} ${TIME_CHUNKS[i].name}${pluralize(count)}`;
            break;
        }
    }
    if (deltaSeconds > 60 * 60 * 24 && i + 1 < TIME_CHUNKS.length) {
        const time = TIME_CHUNKS[i + 1];
        const count2 = Math.floor((deltaSeconds - seconds * count) / time.seconds);
        if (count2 !== 0) {
            result = `${result}, ${count2} ${time.name}${pluralize(count2)}`;
        }
    }
    result = `${result} ${ending(isFuture)}`;
    return result;
}
/**
 * Generate a description of how much time has passed since a broadcast start Date, used for room cards
 * @param date the reference point to be compared against the current date/time
 * @returns a localized string describing the elapsed time rounded to the nearest appropriate whole time unit --
 * except for values returned in hours, which are rounded to a single decimal point.
 * Possible units span from minutes to years (though that would be a crazy-long broadcast.) Values returned in
 * minutes or hours (i.e. almost all of them) are abbreviated to "min" / "hrs" but other units are not abbrev'd.
 * Ex: "1 min"; "21 mins"; "1.0 hrs"; "8.9 hrs"; "2 days"; "1 week"; "5 months"
 */
function getTimeOnline(date) {
    const elapsedSeconds = (new Date().getTime() - date.getTime()) / 1000;
    const getConvertedValueTranslation = (index) => {
        let convertedValue = elapsedSeconds / TIME_CHUNKS[index].seconds;
        convertedValue =
            index === HOUR_INDEX
                ? truncateHoursDecimal(convertedValue)
                : Math.floor(convertedValue);
        const abbreviate = index === HOUR_INDEX || index === MINUTE_INDEX;
        return TIME_CHUNKS[index].translation(convertedValue, abbreviate);
    };
    // Iterate through the TIME_CHUNKS units from large to small, *stopping at minutes*
    // Return the largest time chunk for which the elapsed time is at least 1 unit
    for (let i = 0; i <= MINUTE_INDEX; i += 1) {
        if (elapsedSeconds >= TIME_CHUNKS[i].seconds) {
            return getConvertedValueTranslation(i);
        }
    }
    // If less than one minute has elapsed, render the value as minutes anyway
    return getConvertedValueTranslation(MINUTE_INDEX);
}
/**
 * Generate a description of how much time has passed since a broadcast ended, used for room card details
 * @param date the reference point to be compared against the current date/time
 * @returns a localized string describing when the last broadcast ended, to the nearest appropriate whole unit.
 * Resolution spans from seconds to years, and returns "now" if less than 1 second has elapsed.
 * Ex: "30 seconds ago"; "1 minute ago"; "5 hours ago"; "3 weeks ago"; "1 month ago"
 */
function getLastBroadcast(date) {
    const elapsedSeconds = (new Date().getTime() - date.getTime()) / 1000;
    for (let i = 0; i <= TIME_CHUNKS.length; i += 1) {
        const unit = TIME_CHUNKS[i];
        if (elapsedSeconds >= unit.seconds) {
            return unit.ago(Math.floor(elapsedSeconds / unit.seconds));
        }
    }
    return i18n.now; // elapsed time is less than 1 second
}
/**
 * @param isOffline determines whether the timestamp us treated as the time of the
 *     *previous* broadcast (true) or as the start time of the *current* broadcast (false)
 * @param timestamp a datetime represented in *seconds* since the Unix epoch
 * @returns an appropriate localized string describing either the current broadcast length or the time since
 * the last broadcast, depending on whether the user is online or offline. Used for room card details.
 */
export function getDisplayedTime(isOffline, timestamp) {
    if (timestamp === undefined || timestamp === 0) {
        if (isOffline) {
            return i18n.never;
        }
        return "";
    }
    const utcDate = new Date(timestamp * 1000);
    if (isOffline) {
        // return naturalTime(new Date(), utcDate)
        return getLastBroadcast(utcDate);
    }
    else {
        return getTimeOnline(utcDate);
    }
}
/**
 * Displayed broadcast time is not rounded to the nearest tenth, but is actually truncated at that 1 decimal place.
 * This util function exists because there seems to be no built-in way to truncate or floor for decimals
 * @param hours is the number to be formatted for broadcast time display
 */
export function truncateHoursDecimal(hours) {
    // Example: 13.58 -> 13.5
    const floorNum = Math.floor(hours * 10);
    return floorNum / 10;
}
